import { useContext, useEffect, useRef, useCallback } from 'react';
import { AuthContext } from '../../../../context/auth';
import { PlayerContext } from 'src/context/player';
import { idleLogoutTime, WATCH_TIME_BUFFER, STORAGE_KEYS } from './constants';
import moment from 'moment';

const USER_ACTIVITY_EVENTS = [
  'load', 'click', 'scroll', 'keypress'
] as const;

const setStorageItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`Failed to set localStorage item ${key}:`, error);
    return false;
  }
};

const getStorageItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`Failed to get localStorage item ${key}:`, error);
    return null;
  }
};

const removeStorageItem = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove localStorage item ${key}:`, error);
    return false;
  }
};

export const useAppLogout = () => {
  const authContext = useContext(AuthContext);
  const { playerPaused, playerActive } = useContext(PlayerContext);
  const { isAuthenticated, logout } = authContext;
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<moment.Moment>(moment());

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Check if user is still actively watching content
  const isUserActivelyWatching = useCallback((): boolean => {
    if (!playerActive) return false;
    
    const watchedTime = getStorageItem(STORAGE_KEYS.WATCHED_TIME);
    if (!watchedTime) return false;
    
    const watchedTimestamp = moment(watchedTime);
    const bufferTime = moment().subtract(WATCH_TIME_BUFFER, 'seconds');
    
    return watchedTimestamp.isAfter(bufferTime);
  }, [playerActive]);

  // Check if user was idle when app loads (BEFORE overwriting timestamp)
  const checkIdleOnMount = useCallback((): boolean => {
    const timestamp = getStorageItem(STORAGE_KEYS.LAST_TIMESTAMP);
    if (!timestamp) {
      return false;
    }

    const timestampMoment = moment(timestamp);
    const idleExpiry = timestampMoment.clone().add(idleLogoutTime, 'milliseconds');
    const hasBeenIdle = moment().isAfter(idleExpiry);

    const watchedTime = getStorageItem(STORAGE_KEYS.WATCHED_TIME);
    const watchedMoment = watchedTime ? moment(watchedTime) : null;
    const watchedExpiry = watchedMoment ? watchedMoment.clone().add(WATCH_TIME_BUFFER, 'seconds') : null;
    const isNotWatching = watchedExpiry ? moment().isAfter(watchedExpiry) : true;

    const shouldLogout = hasBeenIdle && isNotWatching;
    return shouldLogout;
  }, []);

  // Start the logout timer
  const startLogoutTimer = useCallback((timerValue: number = idleLogoutTime) => {
    clearTimer();
    
    const currentTime = moment();
    lastActivityRef.current = currentTime;
    setStorageItem(STORAGE_KEYS.LAST_TIMESTAMP, currentTime.toISOString());
    
    timerRef.current = setTimeout(() => {
      // If player is active, check if user is still watching
      if (playerActive) {
        if (isUserActivelyWatching()) {
          // User is still watching, restart timer
          startLogoutTimer(timerValue);
          return;
        }
      }
      
      logout();
    }, timerValue);
  }, [playerActive, isUserActivelyWatching, logout, clearTimer]);

  const handleUserActivity = useCallback((event:any) => {
    const now = moment();
    const timeSinceLastActivity = now.diff(lastActivityRef.current, 'seconds');
    
    // Only reset timer if it's been more than 5 seconds since last activity
    // This prevents excessive timer resets during continuous activity
    if (timeSinceLastActivity >= 5) {
      startLogoutTimer();
    }
  }, [startLogoutTimer]);

  useEffect(() => {

    if (!isAuthenticated) {
      clearTimer();
      return;
    }

    const shouldLogout = checkIdleOnMount();

    // IMPORTANT: Check idle BEFORE overwriting timestamp (prevents race condition)
    if (shouldLogout) {
      logout();
      return; // Don't start timer - user is being logged out
    }
    // User was not logged out with check idle, safe to start timer
    startLogoutTimer();

    USER_ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Listen for storage changes from other tabs to sync activity
    const handleStorageChange = (e: StorageEvent) => {
      // Only react to changes in LAST_TIMESTAMP or WATCHED_TIME key from other tabs
      if ((e.key === STORAGE_KEYS.LAST_TIMESTAMP || e.key === STORAGE_KEYS.WATCHED_TIME) && e.newValue) {
        // Another tab updated the timestamp, restart our timer
        startLogoutTimer();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearTimer();
      USER_ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, handleUserActivity, startLogoutTimer, clearTimer, logout, checkIdleOnMount]);

  useEffect(() => {

    // Don't start timer if user should be logged out
    const shouldLogout = checkIdleOnMount();

    if (isAuthenticated && !shouldLogout) {
      const debounceTimer = setTimeout(() => {
        startLogoutTimer();
      }, 100);

      return () => clearTimeout(debounceTimer);
    } else {
    }
  }, [isAuthenticated, playerPaused, playerActive, startLogoutTimer, checkIdleOnMount]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);


  //Visibilitychange event was added just to check if user need's to be logout issue was relate to Safari mobile.
  //Safari mobile couldn't redirect user to logout page.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        // Check if idle timer expired while page was hidden
        const shouldLogout = checkIdleOnMount();
        if (shouldLogout) {
          logout();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, logout, checkIdleOnMount]);

  return { 
    handleLogoutTimer: startLogoutTimer,
    clearLogoutTimer: clearTimer 
  };
};